<template>
  <div
    :id="`select-${uid}`"
    tabindex="0"
    role="button"
    aria-haspopup="listbox"
    ref="selectEl"
    class="relative cursor-pointer select-none focus:outline-none focus-visible:focus-within:underline truncate md:overflow-visible md:whitespace-normal"
    :aria-labelledby="hiddenLabel || `label-${uid}`"
    :aria-expanded="isOpen"
    @click="selectClick"
    @keydown.enter.prevent.self="toggleSelect"
  >
    <input
      v-if="requiredMessage"
      ref="requiredEl"
      class="pt-16 opacity-0 pointer-events-none absolute z-0"
      :value="selectedItem"
      required
      type="range"
    />
    <!-- type="range" – This is a hack to avoid the default text input type that triggers the keyboard to open on mobile devices -->
    <label
      v-if="label"
      class="text-12 font-bold text-gray-800 opacity-80 uppercase"
      :class="labelClass"
      :id="`label-${uid}`"
    >
      {{ label }}
    </label>
    <SelectedValue
      :class="[selectionClass, { 'border-3 rounded-6 pl-3 h-16': outlined }]"
      :open="isOpen"
      :no-arrow="noArrow"
      :selection-text-class="selectionTextClass"
    >
      <slot v-if="selectedItem" name="selection" :selected-item="selectedItem">
        {{ selectedItem[itemText] }}
      </slot>
      <slot v-else name="placeholder">
        <span class="font-normal text-gray-400">{{ placeholder }}</span>
      </slot>
    </SelectedValue>
    <DrawerPopover
      :open="isOpen"
      :popover-class="popoverClass"
      :anchor-el="$el"
      :no-overlay="noOverlay"
      :mobile-fullscreen="filter"
    >
      <div
        ref="list"
        tabindex="-1"
        v-auto-focus="!filter"
        v-click-outside="onClickOutside"
        class="flex flex-col outline-none bg-white p-4 md:p-5 pb-safe-area-bottom h-full md:rounded-12"
        :class="{ 'rounded-t-12': !filter }"
        @keydown.up.prevent="onMoveUp"
        @keydown.down.prevent="onMoveDown"
        @keydown.enter.prevent="onEnter"
        @keydown.esc.prevent="onEsc"
        @keydown.tab.prevent="onEsc"
      >
        <p :class="headerClass || 'mb-7 mt-3 mr-5 text-20 font-semibold md:hidden'">
          {{ headerText }}
        </p>
        <slot name="header" />
        <SelectFilter
          v-if="filter"
          v-auto-focus
          :value="filterText"
          :placeholder="filterPlaceholder || $t('type_here')"
          @text-change="searchByTerm"
        />
        <SelectList
          v-if="filteredItems.length"
          class="md:max-h-72 justify-end"
          :labelled-by="hiddenLabel || `label-${uid}`"
          :filtered-items="filteredItems"
          :item-value="itemValue"
          :current-item-index="currentItemIndex"
          :value="modelValue"
          @select-item="selectItem"
        >
          <template #default="{ item }">
            <slot name="item" :item="item">
              {{ item[itemText] }}
            </slot>
          </template>
        </SelectList>
        <slot v-else name="no-data">
          <span class="py-2 px-5 italic text-gray-500">No item found...</span>
        </slot>
      </div>
    </DrawerPopover>
  </div>
</template>

<script lang="ts" setup generic="T extends Item">
import removeAccents from 'remove-accents'
import { ClickOutside as vClickOutside } from '@core/directives/click-outside'
import { AutoFocus as vAutoFocus } from '@core/directives/auto-focus'
import type { Item } from './types'
import SelectedValue from './selected-value.vue'
import SelectList from './select-list.vue'
import SelectFilter from './select-filter.vue'
import DrawerPopover from './drawer-popover.vue'

const uid = useId()

const props = withDefaults(
  defineProps<{
    label?: string
    hiddenLabel?: string
    labelClass?: string
    headerText: string
    headerClass?: string
    selectionClass?: string
    selectionTextClass?: string
    modelValue: string | null
    items: T[]
    itemText?: string
    itemValue?: string
    placeholder?: string
    filter?: boolean
    filterPlaceholder?: string
    filterBy?: string[]
    outlined?: boolean
    noOverlay?: boolean
    noArrow?: boolean
    popoverClass?: string
    requiredMessage?: string
  }>(),
  {
    itemText: 'text',
    itemValue: 'value',
  },
)

const emit = defineEmits(['update:modelValue', 'escape'])

const filterText = ref('')
const cursorIndex = ref<number | null>(null)

const filteredItems = computed(() => {
  if (props.filter) {
    const noAccentFilter = removeAccents(filterText.value).toLowerCase()

    return props.items.filter((item) => {
      const fields = props.filterBy ?? [props.itemText]
      const value = fields
        .map((field) =>
          field.split('.').reduce((prev: unknown, curr) => {
            // @ts-expect-error this can return string, number or undefined
            return prev ? prev[curr] : ''
          }, item),
        )
        .join('')
      const noAccentValue = removeAccents(value).toLowerCase()
      return noAccentValue.includes(noAccentFilter)
    })
  }
  return props.items
})

const selectedItem = computed(() => {
  return props.items.find((item) => item[props.itemValue] === props.modelValue)
})

const selectedItemIndex = computed(() => {
  const itemIndex = filteredItems.value.findIndex(
    (item) => item[props.itemValue] === props.modelValue,
  )
  return itemIndex > -1 ? itemIndex : undefined
})

const currentItemIndex = computed(() => {
  return cursorIndex.value ?? selectedItemIndex.value ?? 0
})

const list = useTemplateRef('list')
function selectClick(e: Event) {
  if (!list.value || (e.target && !list.value.contains(e.target as Node))) {
    toggleSelect()
  }
}

const isOpen = ref(false)

function toggleSelect() {
  if (isOpen.value) {
    closeSelect()
  } else {
    openSelect()
  }
}

function openSelect() {
  filterText.value = ''
  isOpen.value = true
  cursorIndex.value = null
  if (!props.noOverlay) {
    document.body.classList.add('overflow-hidden', 'md:overflow-auto')
  }
}

const requiredEl = useTemplateRef('requiredEl')

function toggleRequireValidation(value?: unknown) {
  if (props.requiredMessage) {
    requiredEl.value?.setCustomValidity(value === undefined ? props.requiredMessage : '')
  }
}

onMounted(() => {
  toggleRequireValidation(selectedItem.value)
})

function closeSelect() {
  filterText.value = ''
  isOpen.value = false
  cursorIndex.value = null
  if (!props.noOverlay) {
    document.body.classList.remove('overflow-hidden', 'md:overflow-auto')
  }
}

const selectEl = useTemplateRef('selectEl')

function onClickOutside(event: Event) {
  const target = event.target as HTMLElement
  if (selectEl.value?.contains(target)) {
    return
  }
  closeSelect()
  emit('escape')
}

function selectItem(item: T) {
  emit('update:modelValue', item[props.itemValue])
  toggleRequireValidation(item[props.itemValue])
  closeSelect()
}

function onEnter() {
  if (currentItemIndex.value in filteredItems.value) {
    selectItem(filteredItems.value[currentItemIndex.value])
    restoreFocus()
  }
}

function onEsc() {
  closeSelect()
  restoreFocus()
  emit('escape')
}

function restoreFocus() {
  selectEl.value?.focus()
}

function onMoveUp() {
  if (currentItemIndex.value > 0) {
    cursorIndex.value = currentItemIndex.value - 1
  }
}

function onMoveDown() {
  const lastItem = filteredItems.value.length - 1
  if (currentItemIndex.value < lastItem) {
    cursorIndex.value = currentItemIndex.value + 1
  }
}

function searchByTerm(inputValue: string) {
  filterText.value = inputValue
  cursorIndex.value = 0
}
</script>
